import React, { useEffect, useRef, useState } from 'react';
import { ExchangeBanner, ExchangeModal } from '|/app/header/exchange-banner';
import ImpersonationBanner from '|/app/header/impersonation-baner';
import { LockTimerModal } from '|/app/header/purchase-header/components/lock-timer';
import SlateHeader from '|/app/header/slate-header';
import { Summary } from '|/app/services';
import LinkButton from '|/components/carvana-ds/link-button';
import { SupportMfeModal } from '|/components/support-contact';
import { useSlate } from '|/context';
import { isCarvanaNativeApp } from '|/helpers/is-native-app';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context/use-analytics-context';
import styles from './checkout-header.module.css';
import HeaderContent from './header-content';

/**
 * Published so anything pinned below the sticky header can offset by its real height instead
 * of a guess. The header is not a fixed size — the native webview adds a 48px bar above it,
 * and its padding differs by viewport — and a stale constant shows up as content sliding
 * under the header before it sticks.
 */
export const CHECKOUT_HEADER_HEIGHT_VAR = '--pp-checkout-header-height';

interface CheckoutHeaderProps {
  orderSummary?: Summary | null;
  /**
   * Rolls the order back to the cart. Supplied only by the `/checkout` route — the
   * insurance-options route renders this header too but has no such affordance.
   */
  onEditOrder?: () => void;
}

const CheckoutHeader: React.FC<CheckoutHeaderProps> = ({ orderSummary = null, onEditOrder }) => {
  const isMobileNative = isCarvanaNativeApp();
  const { isSlateSkin } = useSlate();
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isLockTimerModalOpen, setIsLockTimerModalOpen] = useState(false);
  const { trackEvent } = useAnalyticsContext();
  const headerRef = useRef<HTMLElement>(null);

  // Declared before the Slate early return so hook order stays stable; the ref is simply
  // never attached on that branch and the effect no-ops.
  useEffect(() => {
    const element = headerRef.current;
    if (!element) return;

    const publishHeight = () => {
      document.documentElement.style.setProperty(
        CHECKOUT_HEADER_HEIGHT_VAR,
        `${element.getBoundingClientRect().height}px`
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(element);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(CHECKOUT_HEADER_HEIGHT_VAR);
    };
  }, []);

  // Checkout shows the order summary inline (vehicle card), so the Slate header omits the chip.
  if (isSlateSkin) {
    return <SlateHeader inlineBrand={true} />;
  }

  const onLogoClick = () => {
    trackEvent('pp_clicked_logo_icon');
    window.location.href = '/';
  };

  const onLockTimerClick = () => {
    trackEvent('pp_clicked_lock_timer');
    setIsLockTimerModalOpen(true);
  };

  const handleSupportClick = () => {
    setIsSupportModalOpen(true);
  };

  const saveAndExit = () => {
    sendNativeEvent({ event: 'save_exit', payload: {}, source: 'purchase' });
  };

  return (
    <>
      <ImpersonationBanner />
      <header className={styles.header} ref={headerRef}>
        {isMobileNative && (
          <div className={styles.nativeHeader}>
            <LinkButton
              variant="inverse"
              size="sm"
              underline={false}
              className={styles.saveExitButton}
              data-analytics-id="pp_header_save_exit_button"
              onClick={saveAndExit}
            >
              Save &amp; exit
            </LinkButton>
          </div>
        )}

        <HeaderContent
          orderSummary={orderSummary}
          onLogoClick={onLogoClick}
          onSupportClick={handleSupportClick}
          onLockTimerClick={onLockTimerClick}
          onEditOrder={onEditOrder}
        />
      </header>

      <ExchangeBanner
        orderSummary={orderSummary}
        onSeeDetails={() => {
          setIsExchangeModalOpen(true);
        }}
      />

      <ExchangeModal
        isOpen={isExchangeModalOpen}
        orderSummary={orderSummary}
        onClose={() => {
          setIsExchangeModalOpen(false);
        }}
      />

      <LockTimerModal
        isOpen={isLockTimerModalOpen}
        onClose={() => {
          setIsLockTimerModalOpen(false);
        }}
      />

      <SupportMfeModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
      />
    </>
  );
};

export default CheckoutHeader;
